import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlagIcon from '@mui/icons-material/Flag';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';

/**
 * Map icon string to MUI icon component.
 */
const ICON_MAP = {
  fitness_center: FitnessCenterIcon,
  local_fire_department: LocalFireDepartmentIcon,
  emoji_events: EmojiEventsIcon,
  people: PeopleIcon,
  restaurant: RestaurantIcon,
  photo_camera: PhotoCameraIcon,
  flag: FlagIcon,
  handshake: HandshakeIcon,
  edit: EditIcon,
};

/**
 * Tier color mapping using theme achievement colors.
 */
const TIER_COLORS = {
  gold: { ring: '#FFD700', bg: '#FFF3B0' },
  silver: { ring: '#C0C0C0', bg: '#E8E8E8' },
  bronze: { ring: '#CD7F32', bg: '#F0D9B5' },
};

/**
 * Achievement badge component.
 * Displays a circular icon with a tier-colored ring.
 * Shows greyed out with lock for locked achievements.
 *
 * @param {Object} props
 * @param {string} props.icon - Icon key from ICON_MAP.
 * @param {string} props.tier - 'bronze' | 'silver' | 'gold'.
 * @param {string} props.name - Achievement name.
 * @param {string} props.description - Achievement description.
 * @param {boolean} [props.unlocked=false] - Whether the achievement is unlocked.
 * @param {boolean} [props.compact=false] - Compact display mode.
 */
export const AchievementBadge = ({
  icon,
  tier = 'bronze',
  name,
  description,
  unlocked = false,
  compact = false,
}) => {
  const IconComponent = unlocked ? (ICON_MAP[icon] || EmojiEventsIcon) : LockIcon;
  const colors = TIER_COLORS[tier] || TIER_COLORS.bronze;
  const size = compact ? 48 : 64;
  const iconSize = compact ? 22 : 28;

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="caption">
            {description}
          </Typography>
          {!unlocked && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Not yet unlocked
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          opacity: unlocked ? 1 : 0.4,
          cursor: 'default',
        }}
      >
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: `3px solid ${unlocked ? colors.ring : '#999'}`,
            backgroundColor: unlocked ? colors.bg : 'action.disabledBackground',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: unlocked ? `0 0 12px ${colors.ring}40` : 'none',
            '&:hover': unlocked ? {
              transform: 'scale(1.1)',
              boxShadow: `0 0 20px ${colors.ring}60`,
            } : {},
          }}
        >
          <IconComponent
            sx={{
              fontSize: iconSize,
              color: unlocked ? colors.ring : 'text.disabled',
            }}
          />
        </Box>
        {!compact && (
          <Typography
            variant="caption"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              maxWidth: size + 16,
              lineHeight: 1.2,
              color: unlocked ? 'text.primary' : 'text.disabled',
            }}
          >
            {name}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default AchievementBadge;
